/**
 * Theme Configurations for Scoop Analytics
 *
 * Each theme is a collection of CSS custom property overrides.
 * These values are applied to the document root to change the app's appearance.
 *
 * To add a new theme:
 * 1. Add a new theme object to the THEMES object
 * 2. Define color values for all CSS custom properties
 * 3. The theme will automatically be available in the theme switcher
 */

export const THEME_IDS = {
    LIGHT: 'light',
    DARK: 'dark',
    // Experimental palettes (theme-test evaluations — allowlist-gated in the
    // account menu, NOT product themes)
    EXP_BLUE: 'exp_blue',
    EXP_ORANGE: 'exp_orange',
    EXP_TEAL: 'exp_teal',
}

export const THEMES = {
    [THEME_IDS.LIGHT]: {
        id: THEME_IDS.LIGHT,
        name: 'Light',
        description: 'Clean and bright interface',
        colors: {
            // Primary Brand Colors
            '--scoop-color-primary-lighter': '#FFF5F7',
            '--scoop-color-primary-light': '#FFE7EE',
            '--scoop-color-primary': '#E50B54',
            '--scoop-color-primary-hover': '#C6093F',

            // Dark Colors
            '--scoop-color-dark-bg': '#4A3C4E',
            '--scoop-color-dark-tertiary': '#412747',
            '--scoop-color-dark-secondary': '#1A0B2E',
            '--scoop-color-dark': '#130417',

            // Text Colors
            '--scoop-color-text-inverse': '#FFFFFF',
            '--scoop-color-text-muted': '#CCCCCC',
            '--scoop-color-text-light': '#B5B5B5',
            '--scoop-color-text-tertiary': '#979099',
            '--scoop-color-text-secondary': '#635566',
            '--scoop-color-text-primary': '#1A0B2E',

            // Background Colors
            '--scoop-bg-light': '#FFFFFF',
            '--scoop-bg-highlight': '#FFF5F7',
            '--scoop-bg-auth': '#EEF2FF',
            '--scoop-bg-primary': '#FFFFFF',
            '--scoop-bg-secondary': '#FAFAFA',
            '--scoop-bg-tertiary': '#F0F0F0',
            '--scoop-bg-dark': '#130417',

            // Border Colors
            '--scoop-border-color-lighter': '#FAFAFA',
            '--scoop-border-color-light': '#D4D4D4',
            '--scoop-border-color': '#D0CDD1',
            '--scoop-border-color-dark': '#A0A0A0',
            '--scoop-border-color-focus': '#E50B54',
            '--scoop-border-color-hover': '#E50B54',

            // State Colors — CANONICAL RAMP (2026-07-28, Brad decision): the
            // portal's fresher palette, encoded once for every surface.
            // "-strong" variants are the portal's own text-safe values for
            // small text (the base hues don't pass AA at 12px).
            '--scoop-color-success': '#12B76A',
            '--scoop-color-warning': '#DC6803',
            '--scoop-color-error': '#B42318',
            '--scoop-color-info': '#175CD3',
            '--scoop-color-success-strong': '#027A48',
            '--scoop-color-warning-strong': '#B54708',
            // Six-tier severity completion (2026-07-28): Critical and Watch are
            // canonical tiers, not aliases. Critical must out-shout Severe and
            // Watch must be distinguishable from Moderate, or the two tiers the
            // engine emits collapse visually right after we un-collapsed them.
            '--scoop-color-critical': '#7A271A',
            '--scoop-color-watch': '#CA8504',
            '--scoop-color-watch-strong': '#A15C07',

            // State soft tints (badge/flash backgrounds — the portal's chip tints)
            '--scoop-color-success-soft': '#ECFDF3',
            '--scoop-color-warning-soft': '#FFFAEB',
            '--scoop-color-error-soft': '#FEF3F2',
            '--scoop-color-info-soft': '#EFF8FF',
            '--scoop-color-critical-soft': '#FEE4E2',
            '--scoop-color-watch-soft': '#FEFBE8',

            // Link Colors
            '--scoop-color-link-hover': '#3B82F6',
            '--scoop-color-link': '#1D4ED8',
            '--scoop-color-link-visited': '#9333EA',
        },
    },

    [THEME_IDS.DARK]: {
        id: THEME_IDS.DARK,
        name: 'Dark',
        description: 'Sleek dark interface with reduced eye strain',
        colors: {
            // Primary Brand Colors (keep brand color, adjust shades)
            '--scoop-color-primary': '#E50B54',
            '--scoop-color-primary-light': '#4A2A38',
            '--scoop-color-primary-lighter': '#2A1A20',
            '--scoop-color-primary-hover': '#C6093F',

            // Dark Colors (inverted for dark mode)
            '--scoop-color-dark': '#E8E8E8',
            '--scoop-color-dark-secondary': '#B8B8B8',
            '--scoop-color-dark-tertiary': '#888888',
            '--scoop-color-dark-bg': '#E8E8E8',

            // Text Colors (light text on dark backgrounds)
            '--scoop-color-text-primary': '#E8E8E8',
            '--scoop-color-text-secondary': '#B8B8B8',
            '--scoop-color-text-tertiary': '#888888',
            '--scoop-color-text-light': '#B8B8B8',
            '--scoop-color-text-muted': '#666666',
            '--scoop-color-text-inverse': '#1A1A1A',

            // Background Colors (dark backgrounds)
            '--scoop-bg-primary': '#1A1A1A',
            '--scoop-bg-secondary': '#242424',
            '--scoop-bg-tertiary': '#2A2A2A',
            '--scoop-bg-light': '#222222',
            '--scoop-bg-highlight': '#2A1A20',
            '--scoop-bg-auth': '#1A1A2A',
            '--scoop-bg-dark': '#E8E8E8',

            // Border Colors (subtle borders for dark mode)
            '--scoop-border-color': '#3A3A3A',
            '--scoop-border-color-light': '#2A2A2A',
            '--scoop-border-color-lighter': '#1E1E1E',
            '--scoop-border-color-dark': '#4A4A4A',
            '--scoop-border-color-focus': '#E50B54',
            '--scoop-border-color-hover': '#E50B54',

            // State Colors (dark variants of the canonical ramp — deep hues
            // don't read on dark surfaces, so dark mode carries the same
            // families lightened; "-strong" equals base here since base passes)
            '--scoop-color-success': '#32D583',
            '--scoop-color-warning': '#F79009',
            '--scoop-color-error': '#F97066',
            '--scoop-color-info': '#84ADFF',
            '--scoop-color-success-strong': '#32D583',
            '--scoop-color-warning-strong': '#F79009',
            // Critical is MORE saturated than error here, not lighter — on dark
            // surfaces saturation reads as urgency where lightness reads as calm.
            '--scoop-color-critical': '#F04438',
            '--scoop-color-watch': '#FAC515',
            '--scoop-color-watch-strong': '#FAC515',

            // State soft tints (~12% of the canonical hue over the dark surface)
            '--scoop-color-success-soft': '#192D24',
            '--scoop-color-warning-soft': '#312317',
            '--scoop-color-error-soft': '#2C1B1A',
            '--scoop-color-info-soft': '#1A2230',
            '--scoop-color-critical-soft': '#3A1A18',
            '--scoop-color-watch-soft': '#2E2A15',

            // Link Colors
            '--scoop-color-link': '#E50B54',
            '--scoop-color-link-hover': '#C6093F',
            '--scoop-color-link-visited': '#B8086B',
        },
    },
}

/**
 * Get theme by ID
 * @param {string} themeId - Theme identifier
 * @returns {Object|null} Theme object or null if not found
 */
export const getTheme = (themeId) => {
    return THEMES[themeId] || null
}

// ==========================================================================
// EXPERIMENTAL PALETTES (theme-test/*.html evaluations)
// Faithful to the test files — INCLUDING their state-color collisions, which
// are the point of the evaluation (see the theme options deep dive,
// 2026-07-28). Allowlist-gated in AccountDropdown; never product themes
// without completing both-mode token sets and resolving the collisions.
// ==========================================================================
const experimental = (id, name, description, overrides) => ({
    id,
    name,
    description,
    colors: { ...THEMES[THEME_IDS.LIGHT].colors, ...overrides },
})

THEMES[THEME_IDS.EXP_BLUE] = experimental(
    THEME_IDS.EXP_BLUE,
    'Corporate Blue (test)',
    'theme-test/01 — evaluation only',
    {
        '--scoop-color-primary-lighter': '#EFF6FF',
        '--scoop-color-primary-light': '#DBEAFE',
        '--scoop-color-primary': '#2563EB',
        '--scoop-color-primary-hover': '#1D4ED8',
        '--scoop-color-text-primary': '#1E3A8A',
        '--scoop-color-text-secondary': '#3B82F6',
        '--scoop-bg-highlight': '#EFF6FF',
        '--scoop-border-color-focus': '#2563EB',
        '--scoop-border-color-hover': '#2563EB',
        '--scoop-color-link': '#2563EB',
        '--scoop-color-link-hover': '#1D4ED8',
    }
)

THEMES[THEME_IDS.EXP_ORANGE] = experimental(
    THEME_IDS.EXP_ORANGE,
    'Warm Orange (test)',
    'theme-test/03 — evaluation only; warning==primary by design of the test',
    {
        '--scoop-color-primary-lighter': '#FFF7ED',
        '--scoop-color-primary-light': '#FFEDD5',
        '--scoop-color-primary': '#F97316',
        '--scoop-color-primary-hover': '#EA580C',
        '--scoop-color-text-primary': '#9A3412',
        '--scoop-color-text-secondary': '#F97316',
        '--scoop-bg-highlight': '#FFF7ED',
        '--scoop-border-color-focus': '#F97316',
        '--scoop-border-color-hover': '#F97316',
        '--scoop-color-link': '#F97316',
        '--scoop-color-link-hover': '#EA580C',
        // The test file's literal collision — warning becomes the brand color:
        '--scoop-color-warning': '#F97316',
        '--scoop-color-warning-soft': '#FFEDD5',
    }
)

THEMES[THEME_IDS.EXP_TEAL] = experimental(
    THEME_IDS.EXP_TEAL,
    'Modern Teal (test)',
    'theme-test/05 — evaluation only; info==primary by design of the test',
    {
        '--scoop-color-primary-lighter': '#F0FDFA',
        '--scoop-color-primary-light': '#CCFBF1',
        '--scoop-color-primary': '#0D9488',
        '--scoop-color-primary-hover': '#0F766E',
        '--scoop-color-text-primary': '#134E4A',
        '--scoop-color-text-secondary': '#0D9488',
        '--scoop-bg-highlight': '#F0FDFA',
        '--scoop-border-color-focus': '#0D9488',
        '--scoop-border-color-hover': '#0D9488',
        '--scoop-color-link': '#0D9488',
        '--scoop-color-link-hover': '#0F766E',
        // The test file's literal collision — info becomes the brand color:
        '--scoop-color-info': '#0D9488',
        '--scoop-color-info-soft': '#CCFBF1',
    }
)

export const EXPERIMENTAL_THEME_IDS = [
    THEME_IDS.EXP_BLUE,
    THEME_IDS.EXP_ORANGE,
    THEME_IDS.EXP_TEAL,
]

/**
 * Get all available themes
 * @returns {Array} Array of theme objects
 */
export const getAllThemes = () => {
    return Object.values(THEMES)
}

/**
 * Get default theme
 * @returns {Object} Default theme object
 */
export const getDefaultTheme = () => {
    return THEMES[THEME_IDS.LIGHT]
}

/**
 * Detect system theme preference
 * @returns {string} Theme ID based on system preference
 */
export const getSystemThemePreference = () => {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return THEME_IDS.DARK
    }
    return THEME_IDS.LIGHT
}
